import { useState } from "react";
import { Link } from "react-router-dom";
import { faqList, usageGuides } from "@/mocks/site";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import Breadcrumb from "@/components/base/Breadcrumb";
import FeedbackForm from "@/components/feature/FeedbackForm";

export default function HelpPage() {
  useDocumentTitle(
    "帮助与反馈 - 漫蛙2动漫资讯平台",
    "漫蛙2帮助中心，提供常见问题解答、使用指引与反馈入口。"
  );
  const [openId, setOpenId] = useState<string | null>(faqList[0]?.id ?? null);

  const toggle = (id: string) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <div className="pb-4">
      <section className="border-b border-background-200 bg-background-100">
        <div className="page-wrap py-8 md:py-12">
          <Breadcrumb items={[{ label: "帮助与反馈" }]} />
          <h1 className="mt-4 text-2xl font-extrabold text-foreground-950 md:text-3xl">帮助与反馈</h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里有常见问题解答、使用指引以及反馈入口。如果仍然没有解决你的问题，欢迎直接提交反馈。
          </p>
        </div>
      </section>

      <section className="page-wrap mt-10">
        <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">使用指引</h2>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {usageGuides.map((guide) => (
            <div key={guide.id} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className={`${guide.icon} text-2xl`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 text-base font-bold text-foreground-950">{guide.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{guide.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="page-wrap mt-14 grid grid-cols-1 gap-10 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">常见问题解答</h2>
          <ul className="mt-6 space-y-3">
            {faqList.map((faq) => {
              const open = openId === faq.id;
              return (
                <li key={faq.id} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  <button
                    type="button"
                    onClick={() => toggle(faq.id)}
                    aria-expanded={open}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <span className="text-sm font-semibold text-foreground-950 md:text-base">{faq.question}</span>
                    <i
                      className={`shrink-0 text-lg text-primary-500 ${open ? "ri-subtract-line" : "ri-add-line"}`}
                      aria-hidden="true"
                    ></i>
                  </button>
                  {open && (
                    <div className="border-t border-background-200 px-5 py-4 text-sm leading-relaxed text-foreground-600">
                      {faq.answer}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="mt-8 rounded-lg bg-primary-50 p-5">
            <h3 className="text-base font-bold text-foreground-950">还有问题想了解？</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">
              可以先看看「关于我们」了解网站定位，或直接使用右侧反馈表单联系我们。
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex whitespace-nowrap items-center gap-1.5 rounded-md border border-primary-300 bg-background-50 px-4 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
              >
                <i className="ri-information-line"></i>
                关于我们
              </Link>
              <Link
                to="/community"
                className="inline-flex whitespace-nowrap items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-chat-smile-2-line"></i>
                前往社区
              </Link>
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">反馈入口</h2>
          <p className="mt-2 text-sm text-foreground-600">提交后我们会尽快查看并回复。</p>
          <div className="mt-5">
            <FeedbackForm />
          </div>
        </div>
      </section>
    </div>
  );
}